import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { listStoreUniformEmployees } from '../../../services/storeUniformFormApi';

export const badge = (status = '') => {
  const value = String(status || '').toLowerCase();

  if (value === 'approved' || value === 'provided') return 'success';
  if (value === 'rejected') return 'danger';
  if (value === 'submitted') return 'warning';
  if (value === 'pending') return 'secondary';

  return 'secondary';
};

const empty = { page: 1, limit: 20, total: 0, totalPages: 0, hasNext: false, hasPrev: false };
const TABLE_COLS = 11;
const getErrorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const StoreUniformFormList = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [limit] = useState(20);
  const [pagination, setPagination] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const loadingRef = useRef(false);

  const load = useCallback(async ({ nextPage = 1, nextSearch = '' } = {}) => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const r = await listStoreUniformEmployees({ page: nextPage, limit, search: nextSearch });
      const p = r.data || {};
      setRows(p.items || []);
      setPagination(p.pagination || { ...empty, page: nextPage, limit });
    } catch (e) {
      setError(getErrorMessage(e, 'Unable to load uniform records.'));
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    load({ nextPage: 1, nextSearch: '' });
  }, [load]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);

      navigate(location.pathname, {
        replace: true,
        state: {},
      });
    }
  }, [location, navigate]);

  const handleSearch = () => {
    const value = search.trim();
    setAppliedSearch(value);
    load({ nextPage: 1, nextSearch: value });
  };

  const handleClear = () => {
    setSearch('');
    setAppliedSearch('');
    load({ nextPage: 1, nextSearch: '' });
  };

  const go = (nextPage) => {
    if (loading || nextPage < 1 || nextPage > pagination.totalPages) return;
    load({ nextPage, nextSearch: appliedSearch });
  };

  return (
    <div>
      <div className="d-flex justify-content-between mb-3">
        <h3>Employee Uniform Requests</h3>
      </div>
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setError('')} />
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMessage}
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setSuccessMessage('')} />
        </div>
      )}
      <div className="card p-3 mb-3">
        <div className="row g-2">
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Search employee name, ID or mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
            />
          </div>
          <div className="col-auto">
            <button type="button" className="btn btn-primary" disabled={loading} onClick={handleSearch}>Search</button>
          </div>
          <div className="col-auto">
            <button type="button" className="btn btn-outline-secondary" disabled={loading || (!search && !appliedSearch)} onClick={handleClear}>Clear</button>
          </div>
        </div>
      </div>
      <div className="card table-responsive">
        <table className="table table-hover mb-0">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Temporary Employee ID</th>
              <th>Permanent Employee ID</th>
              <th>Employee Name</th>
              <th>Designation</th>
              <th>Mobile</th>
              <th>Latest Uniform Type</th>
              <th>Latest Size</th>
              <th>Latest Status</th>
              <th>Total Requests</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={TABLE_COLS} className="text-center py-4"><span className="spinner-border spinner-border-sm me-2" />Loading...</td></tr>
            ) : rows.length ? rows.map((r, i) => (
              <tr key={r.id}>
                <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                <td>{r.tempEmployeeId || '-'}</td>
                <td>{r.permanentEmployeeId || '-'}</td>
                <td>{r.employeeName || r.name || '-'}</td>
                <td>{r.designationName || r.designation || '-'}</td>
                <td>{r.mobile || '-'}</td>
                <td>{r.latestUniform?.uniformType || '-'}</td>
                <td>{r.latestUniform?.size || '-'}</td>
                <td>{r.latestUniform ? <span className={`badge bg-${badge(r.latestUniform.status)}`}>{r.latestUniform.status}</span> : '-'}</td>
                <td>{r.totalUniformRequests}</td>
                <td>{r.action === 'add' ? <Link className="btn btn-sm btn-primary" to={`/store/uniform-forms/add/${r.id}`}>Add Uniform</Link> : <Link className="btn btn-sm btn-outline-info" to={`/store/uniform-forms/employee/${r.id}`}>View</Link>}</td>
              </tr>
            )) : (
              <tr><td colSpan={TABLE_COLS} className="text-center py-4">No employees found</td></tr>
            )}
          </tbody>
        </table>
        <div className="d-flex justify-content-between align-items-center p-3">
          <span>Page {pagination.page} of {pagination.totalPages || 0} ({pagination.total} total)</span>
          <div>
            <button type="button" className="btn btn-outline-secondary me-2" disabled={loading || !pagination.hasPrev} onClick={() => go(pagination.page - 1)}>Previous</button>
            <button type="button" className="btn btn-outline-secondary" disabled={loading || !pagination.hasNext} onClick={() => go(pagination.page + 1)}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreUniformFormList;
